import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// PACKAGES
import { Form, Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../../components/Button';

// ICONS
import { ReactComponent as EyeOnIcon } from '../../../assets/svg/eye-on.svg';
import { ReactComponent as EyeOffIcon } from '../../../assets/svg/eye-off.svg';

// TYPES
import { closeModalType } from '../../../components/GlobalModal';

interface AccountPasswordModalTypes {
	accountDetails: {
		childAwareId?: string;
	};
	closeModal?: closeModalType;
}

const AccountPasswordModal: React.FC<AccountPasswordModalTypes> = ({ accountDetails, closeModal }) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	// State for Password Control
	const [showPassword, setShowPassword] = React.useState<boolean>(false);

	const [showNewPassword, setShowNewPassword] = React.useState<boolean>(false);

	const [showConfirmPassword, setShowConfirmPassword] = React.useState<boolean>(false);

	// INITIAL FORM VALUES
	const initialValues = {
		childawareId: accountDetails.childAwareId || '',
		currentPassword: '',
		newPassword: '',
		confirmPassword: ''
	};

	// VALIDATION
	const validationSchema = Yup.object({
		currentPassword: Yup.string()
			.required('Required')
			.min(12, 'Minimum of 12 characters')
			.matches(
				/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{12,}$/,
				'Please enter a valid password'
			),
		newPassword: Yup.string()
			.required('Required')
			.min(12, 'Minimum of 12 characters')
			.matches(
				/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{12,}$/,
				'Please enter a valid password'
			),
		confirmPassword: Yup.string()
			.required('Required')
			.oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
	});

	// SUBMIT
	const submitFormData = async (values: any) => {
		setIsLoading(true);

		const response = await apibridge.postAuthenticationChangePassword({
			newPassword: values.newPassword,
			confirmPassword: values.confirmPassword,
			oldPassword: values.currentPassword
		});

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}
		}
	};

	return (
		<Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={submitFormData}>
			{(formik) => {
				return (
					<Form translate="yes">
						<div className="form-group">
							<label htmlFor="childawareId">ChildAware ID</label>
							<Field name="childawareId" type="text" readOnly className="form-control disabled-input" />
						</div>
						<div className="form-group">
							<label htmlFor="currentPassword">Current password</label>
							<div className="password-wrapper">
								<Field
									name="currentPassword"
									id="currentPassword"
									type={showPassword ? 'text' : 'password'}
									className="form-control"
								/>
								<Button
									className="btn-text"
									buttonIcon={showPassword ? <EyeOffIcon /> : <EyeOnIcon />}
									onClick={() => setShowPassword(!showPassword)}
									type="button"
								></Button>
							</div>
							<ErrorMessage component={'div'} className="error-message" name="currentPassword" />
						</div>
						<div className="form-group">
							<label htmlFor="newPassword">New password</label>
							<div className="password-wrapper">
								<Field
									name="newPassword"
									id="newPassword"
									type={showNewPassword ? 'text' : 'password'}
									className="form-control"
								/>
								<Button
									className="btn-text"
									buttonIcon={showNewPassword ? <EyeOffIcon /> : <EyeOnIcon />}
									onClick={() => setShowNewPassword(!showNewPassword)}
									type="button"
								></Button>
							</div>
							<ErrorMessage component={'div'} className="error-message" name="newPassword" />
						</div>
						<div className="form-group">
							<label htmlFor="confirmPassword">Confirm new password</label>
							<div className="password-wrapper">
								<Field
									name="confirmPassword"
									id="confirmPassword"
									type={showConfirmPassword ? 'text' : 'password'}
									className="form-control"
								/>
								<Button
									className="btn-text"
									buttonIcon={showConfirmPassword ? <EyeOffIcon /> : <EyeOnIcon />}
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									type="button"
								></Button>
							</div>
							<ErrorMessage component={'div'} className="error-message" name="confirmPassword" />

							<ul className="mtop-16">
								<li>Minimum of 12 characters</li>
								<li>At least one lower case letter</li>
								<li>At least one upper case letter</li>
								<li>At least one number</li>
								<li>At least one special character (e.g. ! @ ?)</li>
							</ul>
						</div>

						<Button type="submit" disabled={isLoading} className="btn-primary full-width">
							Save changes
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};

export default AccountPasswordModal;
